import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, formatApiError } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Check, User, FlaskConical, Stethoscope,
  Copy, CheckCircle2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const STEPS = [
  { key: "demographics", label: "Demographics",       icon: User },
  { key: "labs",         label: "Baseline labs",      icon: FlaskConical },
  { key: "clinical",     label: "Clinical & targets", icon: Stethoscope },
];

function StepHeader({ current }) {
  return (
    <div className="flex items-center gap-2 mb-8" data-testid="wizard-stepper">
      {STEPS.map((s, idx) => {
        const Icon = s.icon;
        const done = idx < current;
        const active = idx === current;
        return (
          <React.Fragment key={s.key}>
            <div className="flex items-center gap-2">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-colors ${
                  done ? "bg-[#4ADE80] border-[#4ADE80] text-black"
                    : active ? "border-[#4ADE80] text-[#4ADE80]"
                    : "border-[#272E2B] text-[#A3ACA7]"
                }`}
              >
                {done ? <Check size={16} /> : <Icon size={16} />}
              </div>
              <div className={`hidden md:block text-xs uppercase tracking-wider font-semibold ${
                active ? "text-white" : done ? "text-[#4ADE80]" : "text-[#A3ACA7]"
              }`}>
                {s.label}
              </div>
            </div>
            {idx < STEPS.length - 1 && (
              <div className={`h-px w-8 md:w-16 mx-1 ${done ? "bg-[#4ADE80]" : "bg-[#272E2B]"}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

const FieldLabel = ({ children, ...p }) => (
  <Label className="ros-label" {...p}>{children}</Label>
);

export default function AddPatientWizard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  // Step 1
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");
  const [email, setEmail] = useState("");
  const [mrn, setMrn] = useState("");
  const [sex, setSex] = useState("male");

  // Step 2
  const [scr, setScr] = useState("");
  const [acr, setAcr] = useState("");
  const [bpS, setBpS] = useState("");
  const [bpD, setBpD] = useState("");
  const [labDate, setLabDate] = useState(new Date().toISOString().slice(0, 10));
  const [labSource, setLabSource] = useState("Quest Diagnostics");

  // Step 3
  const [hasDM, setHasDM] = useState(false);
  const [hasHTN, setHasHTN] = useState(false);
  const [onSGLT2, setOnSGLT2] = useState(false);
  const [weight, setWeight] = useState("");
  const [height, setHeight] = useState("");
  const [potassiumMg, setPotassiumMg] = useState("");
  const [sodiumMg, setSodiumMg] = useState("");
  const [phosphorusMg, setPhosphorusMg] = useState("");
  const [proteinG, setProteinG] = useState("");

  const canAdvance = () => {
    if (step === 0) return firstName && lastName && dob && email;
    if (step === 1) return scr && acr && labDate;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const targetsPayload = {};
      if (potassiumMg)  targetsPayload.potassium_mg  = parseInt(potassiumMg, 10);
      if (sodiumMg)     targetsPayload.sodium_mg     = parseInt(sodiumMg, 10);
      if (phosphorusMg) targetsPayload.phosphorus_mg = parseInt(phosphorusMg, 10);
      if (proteinG)     targetsPayload.protein_g     = parseInt(proteinG, 10);

      const payload = {
        demographics: {
          first_name: firstName.trim(), last_name: lastName.trim(),
          dob, email: email.trim().toLowerCase(), mrn: mrn || null, sex,
        },
        labs: {
          serum_creatinine: parseFloat(scr), urine_acr: parseFloat(acr),
          bp_systolic: bpS ? parseInt(bpS, 10) : null,
          bp_diastolic: bpD ? parseInt(bpD, 10) : null,
          lab_date: labDate, lab_source: labSource,
        },
        clinical: {
          has_diabetes: hasDM, has_hypertension: hasHTN, on_sglt2i: onSGLT2,
          weight_kg: weight ? parseFloat(weight) : null,
          height_cm: height ? parseFloat(height) : null,
          prescribed_targets: Object.keys(targetsPayload).length > 0
            ? { ...targetsPayload, prescribed_by: user?.name || null }
            : null,
        },
      };
      const res = await api.post("/provider/patients", payload);
      setCreated(res.data);
      toast.success(`${res.data.name} onboarded — baseline eGFR ${res.data.baseline_egfr}, Stage ${res.data.stage}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const copyPassword = () => {
    if (!created?.temp_password) return;
    navigator.clipboard.writeText(created.temp_password);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (created) {
    return (
      <div className="min-h-screen bg-[#080A09] text-white p-6 md:p-10" data-testid="wizard-success">
        <div className="max-w-2xl mx-auto ros-card">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#4ADE80]/15 flex items-center justify-center">
              <CheckCircle2 size={26} className="text-[#4ADE80]" />
            </div>
            <div className="flex-1">
              <div className="ros-label">Patient onboarded</div>
              <h2 className="font-heading text-3xl mt-1">{created.name}</h2>
              <div className="text-sm text-[#A3ACA7] mt-1">{created.email}</div>
            </div>
          </div>

          <div className="ros-divider my-6" />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="ros-card-raised">
              <div className="ros-label" style={{ fontSize: 9 }}>Baseline eGFR</div>
              <div className="ros-metric text-2xl mt-1" data-testid="wizard-baseline-egfr">
                {created.baseline_egfr}
              </div>
              <div className="text-[10px] text-[#A3ACA7]">mL/min/1.73m²</div>
            </div>
            <div className="ros-card-raised">
              <div className="ros-label" style={{ fontSize: 9 }}>Stage</div>
              <div className="ros-metric text-2xl mt-1">{created.stage}</div>
            </div>
            <div className="ros-card-raised">
              <div className="ros-label" style={{ fontSize: 9 }}>ACR</div>
              <div className="ros-metric text-2xl mt-1">{created.baseline_acr}</div>
              <div className="text-[10px] text-[#A3ACA7]">mg/g</div>
            </div>
            <div className="ros-card-raised">
              <div className="ros-label" style={{ fontSize: 9 }}>KFRE 5-yr</div>
              <div className="ros-metric text-2xl mt-1"
                   style={{ color: (created.kfre_5yr || 0) > 10 ? "#F87171" : "#fff" }}>
                {created.kfre_5yr != null ? `${created.kfre_5yr.toFixed(1)}%` : "—"}
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-xl border border-[#FBBF24]/30 bg-[#FBBF24]/5 p-4">
            <div className="text-xs uppercase tracking-wider font-semibold text-[#FBBF24]">
              Temporary password — share out of band
            </div>
            <div className="mt-3 flex items-center gap-3">
              <code className="flex-1 font-mono text-base text-white bg-[#1C201E] px-4 py-2.5 rounded-lg border border-[#272E2B]"
                    data-testid="wizard-temp-password">
                {created.temp_password}
              </code>
              <Button variant="outline" onClick={copyPassword} data-testid="wizard-copy-password"
                      className="rounded-full border-[#272E2B] hover:bg-[#1C201E] gap-2">
                {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
              </Button>
            </div>
            <div className="text-[11px] text-[#A3ACA7] mt-2">
              Ask the patient to sign in with <span className="font-mono text-white">{created.email}</span> and this password.
              They can update it after login.
            </div>
          </div>

          <div className="mt-6 flex gap-3">
            <Button
              onClick={() => navigate("/provider")}
              variant="outline"
              className="rounded-full border-[#272E2B] hover:bg-[#1C201E]"
              data-testid="wizard-done-btn"
            >
              Back to inbox
            </Button>
            <Button
              onClick={() => { setCreated(null); setStep(0);
                setFirstName(""); setLastName(""); setDob(""); setEmail(""); setMrn(""); setSex("male");
                setScr(""); setAcr(""); setBpS(""); setBpD("");
                setHasDM(false); setHasHTN(false); setOnSGLT2(false);
                setWeight(""); setHeight("");
                setPotassiumMg(""); setSodiumMg(""); setPhosphorusMg(""); setProteinG(""); }}
              className="rounded-full bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90 font-semibold"
              data-testid="wizard-add-another"
            >
              Add another patient
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080A09] text-white" data-testid="add-patient-wizard">
      <div className="border-b border-[#272E2B] px-6 md:px-10 py-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/provider")}
            data-testid="wizard-back-btn"
            className="w-10 h-10 rounded-full border border-[#272E2B] flex items-center justify-center text-[#A3ACA7] hover:text-white hover:border-[#4ADE80]/40 transition-colors"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <div className="ros-label">Provider portal</div>
            <h1 className="font-heading text-3xl sm:text-4xl mt-1 tracking-tight">
              Onboard a new patient
            </h1>
          </div>
        </div>
      </div>

      <div className="p-6 md:p-10">
        <div className="max-w-2xl mx-auto">
          <StepHeader current={step} />

          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div key="step-0" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -12 }} className="ros-card space-y-4" data-testid="step-demographics">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <FieldLabel>First name *</FieldLabel>
                    <Input value={firstName} onChange={(e) => setFirstName(e.target.value)}
                           data-testid="wiz-first-name" className="mt-2 bg-[#1C201E] border-[#272E2B]" />
                  </div>
                  <div>
                    <FieldLabel>Last name *</FieldLabel>
                    <Input value={lastName} onChange={(e) => setLastName(e.target.value)}
                           data-testid="wiz-last-name" className="mt-2 bg-[#1C201E] border-[#272E2B]" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <FieldLabel>Date of birth *</FieldLabel>
                    <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)}
                           data-testid="wiz-dob" className="mt-2 bg-[#1C201E] border-[#272E2B]" />
                  </div>
                  <div>
                    <FieldLabel>Sex</FieldLabel>
                    <Select value={sex} onValueChange={setSex}>
                      <SelectTrigger className="mt-2 bg-[#1C201E] border-[#272E2B]" data-testid="wiz-sex">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <FieldLabel>Email (used for login) *</FieldLabel>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                         data-testid="wiz-email" className="mt-2 bg-[#1C201E] border-[#272E2B]"
                         placeholder="patient@example.com" />
                </div>
                <div>
                  <FieldLabel>Medical Record Number (optional)</FieldLabel>
                  <Input value={mrn} onChange={(e) => setMrn(e.target.value)}
                         data-testid="wiz-mrn" className="mt-2 bg-[#1C201E] border-[#272E2B]"
                         placeholder="MRN-1234" />
                </div>
              </motion.div>
            )}

            {step === 1 && (
              <motion.div key="step-1" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -12 }} className="ros-card space-y-4" data-testid="step-labs">
                <p className="text-xs text-[#A3ACA7]">
                  These labs establish the patient&apos;s Day-1 eGFR (CKD-EPI 2021) and KFRE risk. They&apos;re stored as
                  <span className="text-[#4ADE80] font-semibold"> lab-verified</span>.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <FieldLabel>Serum creatinine *</FieldLabel>
                    <Input type="number" step="0.01" value={scr} onChange={(e) => setScr(e.target.value)}
                           data-testid="wiz-scr" className="mt-2 bg-[#1C201E] border-[#272E2B]"
                           placeholder="mg/dL" />
                  </div>
                  <div>
                    <FieldLabel>Urine ACR *</FieldLabel>
                    <Input type="number" value={acr} onChange={(e) => setAcr(e.target.value)}
                           data-testid="wiz-acr" className="mt-2 bg-[#1C201E] border-[#272E2B]"
                           placeholder="mg/g" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <FieldLabel>BP systolic</FieldLabel>
                    <Input type="number" value={bpS} onChange={(e) => setBpS(e.target.value)}
                           data-testid="wiz-bp-s" className="mt-2 bg-[#1C201E] border-[#272E2B]"
                           placeholder="mmHg" />
                  </div>
                  <div>
                    <FieldLabel>BP diastolic</FieldLabel>
                    <Input type="number" value={bpD} onChange={(e) => setBpD(e.target.value)}
                           data-testid="wiz-bp-d" className="mt-2 bg-[#1C201E] border-[#272E2B]"
                           placeholder="mmHg" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <FieldLabel>Lab date *</FieldLabel>
                    <Input type="date" value={labDate} onChange={(e) => setLabDate(e.target.value)}
                           data-testid="wiz-lab-date" className="mt-2 bg-[#1C201E] border-[#272E2B]" />
                  </div>
                  <div>
                    <FieldLabel>Lab source</FieldLabel>
                    <Input value={labSource} onChange={(e) => setLabSource(e.target.value)}
                           data-testid="wiz-lab-source" className="mt-2 bg-[#1C201E] border-[#272E2B]"
                           placeholder="Quest Diagnostics" />
                  </div>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="step-2" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -12 }} className="ros-card space-y-6" data-testid="step-clinical">
                <div>
                  <div className="ros-label mb-3">Comorbidities</div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <label className="ros-card-raised flex items-center gap-3 cursor-pointer">
                      <Checkbox checked={hasDM} onCheckedChange={setHasDM} data-testid="wiz-dm" />
                      <span className="text-sm">Diabetes (Type 2)</span>
                    </label>
                    <label className="ros-card-raised flex items-center gap-3 cursor-pointer">
                      <Checkbox checked={hasHTN} onCheckedChange={setHasHTN} data-testid="wiz-htn" />
                      <span className="text-sm">Hypertension</span>
                    </label>
                    <label className="ros-card-raised flex items-center gap-3 cursor-pointer">
                      <Checkbox checked={onSGLT2} onCheckedChange={setOnSGLT2} data-testid="wiz-sglt2" />
                      <span className="text-sm">On SGLT2 inhibitor</span>
                    </label>
                  </div>
                </div>

                <div>
                  <div className="ros-label mb-3">Body measurements</div>
                  <div className="grid grid-cols-2 gap-4">
                    <Input type="number" value={weight} onChange={(e) => setWeight(e.target.value)}
                           data-testid="wiz-weight" placeholder="Weight (kg)"
                           className="bg-[#1C201E] border-[#272E2B]" />
                    <Input type="number" value={height} onChange={(e) => setHeight(e.target.value)}
                           data-testid="wiz-height" placeholder="Height (cm)"
                           className="bg-[#1C201E] border-[#272E2B]" />
                  </div>
                </div>

                <div>
                  <div className="ros-label mb-2">Prescribed target overrides (optional)</div>
                  <p className="text-[11px] text-[#A3ACA7] mb-3">
                    Leave blank to use general KDIGO guidelines. Set a value here to override.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <FieldLabel>Potassium (mg/day)</FieldLabel>
                      <Input type="number" value={potassiumMg} onChange={(e) => setPotassiumMg(e.target.value)}
                             data-testid="wiz-potassium" placeholder="default 2500"
                             className="mt-2 bg-[#1C201E] border-[#272E2B]" />
                    </div>
                    <div>
                      <FieldLabel>Sodium (mg/day)</FieldLabel>
                      <Input type="number" value={sodiumMg} onChange={(e) => setSodiumMg(e.target.value)}
                             data-testid="wiz-sodium" placeholder="default 2000"
                             className="mt-2 bg-[#1C201E] border-[#272E2B]" />
                    </div>
                    <div>
                      <FieldLabel>Phosphorus (mg/day)</FieldLabel>
                      <Input type="number" value={phosphorusMg} onChange={(e) => setPhosphorusMg(e.target.value)}
                             data-testid="wiz-phosphorus" placeholder="default 900"
                             className="mt-2 bg-[#1C201E] border-[#272E2B]" />
                    </div>
                    <div>
                      <FieldLabel>Protein (g/day)</FieldLabel>
                      <Input type="number" value={proteinG} onChange={(e) => setProteinG(e.target.value)}
                             data-testid="wiz-protein" placeholder="default 60"
                             className="mt-2 bg-[#1C201E] border-[#272E2B]" />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-6 flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              data-testid="wizard-prev-btn"
              className="rounded-full border-[#272E2B] hover:bg-[#1C201E] gap-2"
            >
              <ArrowLeft size={14} /> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button
                onClick={() => setStep((s) => s + 1)}
                disabled={!canAdvance()}
                data-testid="wizard-next-btn"
                className="rounded-full bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90 font-semibold gap-2"
              >
                Continue <ArrowRight size={14} />
              </Button>
            ) : (
              <Button
                onClick={submit}
                disabled={submitting}
                data-testid="wizard-submit-btn"
                className="rounded-full bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90 font-semibold gap-2"
              >
                {submitting ? "Onboarding…" : "Onboard patient"} <Check size={14} />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
